import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronUpDownIcon,
  SearchIcon,
} from "../../components/Icons";
import { useGetAllUsersQuery } from "../../store/apiSlices/adminApi";
import { debounce } from "../../utils";
import type { FullRegistrationFormType as User } from "../../schemas/FullRegistrationFormSchema";
import OnboardClientBtn from "./OnboardClientBtn";

export type AllUsersRes = {
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    limit: number;
  };
  user_data: User[];
};

type SortConfig = {
  key: keyof User | null;
  direction: "asc" | "desc";
};

const assign_options = [
  { label: "Assign Task", value: "" },
  { label: "Yogesh", value: "yogesh" },
  { label: "Akash", value: "akash" },
  { label: "Charlie Brown", value: "charlie_brown" },
];

const table_header = [
  { label: "Customer Name", key: "company_name" },
  { label: "Assign", key: "assign" },
  { label: "Tasks", key: "registered_city" },
  { label: "Messages", key: null },
  // { label: "Drafts", key: null },
  { label: "Quotes", key: null },
  { label: "Ongoing Orders", key: null },
  { label: "History", key: null },
];

export default function CustomerProfilesCard({
  limit,
  showPagination,
}: {
  limit: number;
  showPagination: boolean;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: "asc",
  });
  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useGetAllUsersQuery({
    page,
    search,
    limit,
  });

  const pagination = data?.pagination;
  const users = data?.user_data || [];

  // Debounced search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);
  const debouncedSearch = useCallback(debounce(handleSearch, 500), []);

  const previousPage = () => setPage((p) => Math.max(1, p - 1));
  const nextPage = () =>
    setPage((p) =>
      pagination ? Math.min(p + 1, pagination.total_pages) : p + 1,
    );

  // Sorting function
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return users;
    return [...users].sort((a, b) => {
      const aVal = a[sortConfig.key!] || "";
      const bVal = b[sortConfig.key!] || "";
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return 0;
    });
  }, [users, sortConfig]);

  const requestSort = (key: keyof User) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <section className="card">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Customer Profiles
        </h2>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <OnboardClientBtn />
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search"
              className="input-new !pl-10"
              onChange={debouncedSearch}
            />
          </div>
        </div>
      </div>

      {/* Table with responsive scroll */}
      <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {table_header?.map((col) => (
                <th
                  key={col.label}
                  className="px-4 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap cursor-pointer"
                  onClick={() => col.key && requestSort(col.key)}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {col.key && (
                      <ChevronUpDownIcon className="size-5 text-gray-400" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <TableSkeleton />
            ) : (
              sortedUsers.map((user, idx) => (
                <tr
                  key={user.uid}
                  className={`${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-gray-100 cursor-pointer transition`}
                  onClick={() =>
                    navigate(`/admin/customer-profiles/${user.uid}`)
                  }
                >
                  <td className="px-4 py-3 flex items-center gap-2">
                    <div className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-indigo-500 to-blue-500">
                      {user.company_name.at(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm">{user.company_name}</p>
                      <small className="text-gray-500 text-xs">
                        {user.work_email}
                      </small>
                    </div>
                  </td>
                  <td
                    className="px-4 py-3 text-center text-sm"
                    onClick={(e) => (e.stopPropagation(), null)}
                  >
                    <div className="w-[150px]">
                      <select className="input-new">
                        {assign_options.map((el) => (
                          <option value={el.value} key={el.label}>
                            {el.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  {[...Array(5)].map((item, i) => (
                    <td key={i} className="px-4 py-3 text-center text-sm">
                      <p>0</p>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && users.length === 0 && (
        <p className="text-center text-gray-500 mt-4">No records found.</p>
      )}

      {/* Pagination */}
      {pagination && showPagination && (
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <button
            onClick={previousPage}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:text-gray-400 flex items-center gap-1 hover:bg-gray-100 transition"
          >
            <ArrowLeftIcon className="size-4" /> Previous
          </button>
          <span>
            Page {page} / {pagination.total_pages}
          </span>
          <button
            onClick={nextPage}
            disabled={page === pagination.total_pages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:text-gray-400 flex items-center gap-1 hover:bg-gray-100 transition"
          >
            Next <ArrowRightIcon className="size-4" />
          </button>
        </div>
      )}
    </section>
  );
}

function TableSkeleton({
  rows = 5,
  columns = 7,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr key={rowIdx} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIdx) => (
            <td key={colIdx} className="px-4 py-3">
              <div className="h-4 bg-gray-200 rounded-md w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
